import { useState } from 'react'
import {
  TrendingDown,
  DollarSign,
  Cpu,
  Server,
  CheckCircle2,
  AlertTriangle,
  BarChart3,
  Zap,
} from 'lucide-react'
import styles from './CostOptimizationPage.module.css'

type OptimizationType = 'Right-sizing' | 'Idle Resource' | 'Reserved Instance' | 'Storage Optimization'
type Effort = 'Low' | 'Medium' | 'High'
type RecStatus = 'open' | 'applied' | 'dismissed'

interface Recommendation {
  id: string
  type: OptimizationType
  resource: string
  environment: string
  currentCostPerMonth: number
  optimizedCostPerMonth: number
  savingsPerMonth: number
  effort: Effort
  status: RecStatus
  details: string
}

const MOCK_RECS: Recommendation[] = [
  { id: 'opt-001', type: 'Right-sizing',        resource: 'aks-prod-eastus / worker-pool-2',  environment: 'production', currentCostPerMonth: 1240, optimizedCostPerMonth: 620,  savingsPerMonth: 620,  effort: 'Low',    status: 'open',    details: 'CPU utilization < 20% over 30 days. Downsize from Standard_D8s_v3 to Standard_D4s_v3' },
  { id: 'opt-002', type: 'Idle Resource',        resource: 'aks-demo-eastus (nights)',         environment: 'demo',       currentCostPerMonth: 480,  optimizedCostPerMonth: 96,   savingsPerMonth: 384,  effort: 'Medium', status: 'open',    details: 'Cluster idle 8pm–6am. Implement scale-to-zero with scheduled start/stop' },
  { id: 'opt-003', type: 'Reserved Instance',    resource: 'vm-genai-gpu-prod',               environment: 'production', currentCostPerMonth: 2100, optimizedCostPerMonth: 1260, savingsPerMonth: 840,  effort: 'Low',    status: 'open',    details: 'Convert Pay-As-You-Go to 1-year Reserved Instance for 40% savings' },
  { id: 'opt-004', type: 'Storage Optimization', resource: 'backups-blob-storage-cool',       environment: 'production', currentCostPerMonth: 340,  optimizedCostPerMonth: 85,   savingsPerMonth: 255,  effort: 'Low',    status: 'open',    details: 'Move 90-day-old backups from Cool to Archive tier automatically' },
  { id: 'opt-005', type: 'Right-sizing',        resource: 'aks-dev-eastus / worker-pool-1',   environment: 'dev',        currentCostPerMonth: 620,  optimizedCostPerMonth: 310,  savingsPerMonth: 310,  effort: 'Low',    status: 'open',    details: 'Dev cluster over-provisioned. Reduce node count from 5 to 3' },
  { id: 'opt-006', type: 'Idle Resource',        resource: 'postgres-dev-flexible',           environment: 'dev',        currentCostPerMonth: 195,  optimizedCostPerMonth: 39,   savingsPerMonth: 156,  effort: 'Medium', status: 'open',    details: 'Dev database unused on weekends. Enable auto-pause after 30 min idle' },
  { id: 'opt-007', type: 'Reserved Instance',    resource: 'aks-prod-westus nodes (8x)',      environment: 'production', currentCostPerMonth: 3200, optimizedCostPerMonth: 1920, savingsPerMonth: 1280, effort: 'Low',    status: 'open',    details: 'Switch 8 production nodes to 3-year Reserved for 60% discount' },
  { id: 'opt-008', type: 'Storage Optimization', resource: 'logs-analytics-workspace',        environment: 'production', currentCostPerMonth: 520,  optimizedCostPerMonth: 260,  savingsPerMonth: 260,  effort: 'Medium', status: 'open',    details: 'Reduce log retention from 90 to 30 days for non-compliance logs' },
  // Applied
  { id: 'opt-009', type: 'Right-sizing',        resource: 'pay-edi-pods (reduced replicas)',  environment: 'demo',       currentCostPerMonth: 420,  optimizedCostPerMonth: 210,  savingsPerMonth: 210,  effort: 'Low',    status: 'applied', details: 'Reduced Pay EDI demo replicas from 4 to 2 based on load data' },
  { id: 'opt-010', type: 'Idle Resource',        resource: 'qa-cluster scheduled shutdown',   environment: 'qa',         currentCostPerMonth: 800,  optimizedCostPerMonth: 320,  savingsPerMonth: 480,  effort: 'Medium', status: 'applied', details: 'QA cluster now shuts down at 7pm and restarts at 8am weekdays' },
  { id: 'opt-011', type: 'Storage Optimization', resource: 'artifact-registry-old-images',    environment: 'all',        currentCostPerMonth: 180,  optimizedCostPerMonth: 40,   savingsPerMonth: 140,  effort: 'Low',    status: 'applied', details: 'Automated cleanup policy removes container images older than 60 days' },
  // Dismissed
  { id: 'opt-012', type: 'Reserved Instance',    resource: 'vm-legacy-m2m-prod',             environment: 'production', currentCostPerMonth: 1100, optimizedCostPerMonth: 660,  savingsPerMonth: 440,  effort: 'High',   status: 'dismissed', details: 'Reserved instance not feasible due to planned M2M migration in 3 months' },
]

const TYPE_CONFIG: Record<OptimizationType, { icon: React.ElementType; color: string; bg: string }> = {
  'Right-sizing':        { icon: Cpu,       color: 'var(--dc-deploy)',  bg: 'var(--dc-deploy-bg)'  },
  'Idle Resource':       { icon: Zap,       color: 'var(--dc-observe)', bg: 'var(--dc-observe-bg)' },
  'Reserved Instance':   { icon: DollarSign,color: 'var(--dc-finance)', bg: 'var(--dc-finance-bg)' },
  'Storage Optimization':{ icon: Server,    color: 'var(--dc-ops)',     bg: 'var(--dc-ops-bg)'     },
}

const EFFORT_CONFIG: Record<Effort, string> = {
  Low:    'effortLow',
  Medium: 'effortMedium',
  High:   'effortHigh',
}

function fmt(n: number) {
  return `$${n.toLocaleString()}`
}

export function CostOptimizationPage() {
  const [recs, setRecs] = useState(MOCK_RECS)
  const [typeFilter, setTypeFilter] = useState<OptimizationType | 'all'>('all')
  const [showApplied, setShowApplied] = useState(false)

  const applyRec = (id: string) => {
    setRecs((prev) => prev.map((r) => r.id === id ? { ...r, status: 'applied' as RecStatus } : r))
  }

  const dismissRec = (id: string) => {
    setRecs((prev) => prev.map((r) => r.id === id ? { ...r, status: 'dismissed' as RecStatus } : r))
  }

  const openRecs = recs.filter((r) => r.status === 'open')
  const appliedRecs = recs.filter((r) => r.status === 'applied')

  const totalPotential = openRecs.reduce((sum, r) => sum + r.savingsPerMonth, 0)
  const totalApplied   = appliedRecs.reduce((sum, r) => sum + r.savingsPerMonth, 0)

  const displayRecs = (showApplied ? appliedRecs : openRecs).filter((r) =>
    typeFilter === 'all' || r.type === typeFilter
  )

  const types: OptimizationType[] = ['Right-sizing', 'Idle Resource', 'Reserved Instance', 'Storage Optimization']

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <TrendingDown size={20} />
          </div>
          <div>
            <h1 className={styles.pageTitle}>Cost Optimization</h1>
            <p className={styles.pageSubtitle}>AI-powered recommendations to reduce Azure spending across all environments</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnSecondary}>
            <BarChart3 size={14} />
            Analyze All
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-finance-bg)', color: 'var(--dc-finance)' }}>
            <TrendingDown size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{fmt(totalPotential)}</div>
            <div className={styles.statLabel}>Potential Savings/mo</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--aptean-warning-bg)', color: 'var(--aptean-warning)' }}>
            <AlertTriangle size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{openRecs.length}</div>
            <div className={styles.statLabel}>Open Recommendations</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--aptean-success-bg)', color: 'var(--aptean-success)' }}>
            <CheckCircle2 size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{appliedRecs.length}</div>
            <div className={styles.statLabel}>Applied</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--aptean-success-bg)', color: 'var(--aptean-success)' }}>
            <DollarSign size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{fmt(totalApplied)}</div>
            <div className={styles.statLabel}>Saved This Month</div>
          </div>
        </div>
      </div>

      {/* View Toggle + Type Filter */}
      <div className={styles.filterRow}>
        <div className={styles.viewToggle}>
          <button
            className={`${styles.viewBtn} ${!showApplied ? styles.viewBtnActive : ''}`}
            onClick={() => setShowApplied(false)}
          >
            Open ({openRecs.length})
          </button>
          <button
            className={`${styles.viewBtn} ${showApplied ? styles.viewBtnActive : ''}`}
            onClick={() => setShowApplied(true)}
          >
            Applied ({appliedRecs.length})
          </button>
        </div>
        <div className={styles.typeFilters}>
          <button
            className={`${styles.typeBtn} ${typeFilter === 'all' ? styles.typeBtnActive : ''}`}
            onClick={() => setTypeFilter('all')}
          >
            All Types
          </button>
          {types.map((t) => {
            const cfg = TYPE_CONFIG[t]
            const Icon = cfg.icon
            return (
              <button
                key={t}
                className={`${styles.typeBtn} ${typeFilter === t ? styles.typeBtnActive : ''}`}
                onClick={() => setTypeFilter(t)}
                style={typeFilter === t ? { background: cfg.bg, color: cfg.color, borderColor: cfg.color } : undefined}
              >
                <Icon size={12} />
                {t}
              </button>
            )
          })}
        </div>
      </div>

      {/* Recommendations List */}
      <div className={styles.recList}>
        {displayRecs.map((rec) => {
          const cfg = TYPE_CONFIG[rec.type]
          const Icon = cfg.icon
          return (
            <div key={rec.id} className={`${styles.recCard} ${rec.status === 'applied' ? styles.recCardApplied : ''}`}>
              <div className={styles.recLeft}>
                <div className={styles.recTypeIcon} style={{ background: cfg.bg, color: cfg.color }}>
                  <Icon size={17} />
                </div>
                <div className={styles.recBody}>
                  <div className={styles.recHeader}>
                    <span className={styles.recType} style={{ background: cfg.bg, color: cfg.color }}>
                      {rec.type}
                    </span>
                    <span className={`${styles.effortBadge} ${styles[EFFORT_CONFIG[rec.effort]]}`}>
                      {rec.effort} Effort
                    </span>
                    <span className={styles.envBadge}>{rec.environment}</span>
                  </div>
                  <div className={styles.recResource}>{rec.resource}</div>
                  <div className={styles.recDetails}>{rec.details}</div>
                </div>
              </div>
              <div className={styles.recRight}>
                <div className={styles.costGrid}>
                  <div className={styles.costItem}>
                    <span className={styles.costLabel}>Current</span>
                    <span className={styles.costValue}>{fmt(rec.currentCostPerMonth)}/mo</span>
                  </div>
                  <div className={styles.costItem}>
                    <span className={styles.costLabel}>Optimized</span>
                    <span className={styles.costValueOpt}>{fmt(rec.optimizedCostPerMonth)}/mo</span>
                  </div>
                  <div className={styles.costItem}>
                    <span className={styles.costLabel}>Save</span>
                    <span className={styles.costValueSave}>
                      <TrendingDown size={13} />
                      {fmt(rec.savingsPerMonth)}/mo
                    </span>
                  </div>
                </div>
                {rec.status === 'open' && (
                  <div className={styles.recActions}>
                    <button className={styles.dismissBtn} onClick={() => dismissRec(rec.id)}>Dismiss</button>
                    <button className={styles.applyBtn} onClick={() => applyRec(rec.id)}>
                      <CheckCircle2 size={13} />
                      Apply
                    </button>
                  </div>
                )}
                {rec.status === 'applied' && (
                  <div className={styles.appliedTag}>
                    <CheckCircle2 size={14} />
                    Applied
                  </div>
                )}
              </div>
            </div>
          )
        })}
        {displayRecs.length === 0 && (
          <div className={styles.emptyState}>
            <CheckCircle2 size={32} />
            <p>{showApplied ? 'No applied recommendations yet.' : 'No open recommendations match your filter.'}</p>
          </div>
        )}
      </div>
    </div>
  )
}
